import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { btnStyle } from './theme';
import ProxyJobList from './ProxyJobList';

// Proxy generation only needs a source folder + a list of files to read from
// it (see main.js's proxy:enqueue handler -- it resolves each file's
// absolutePath from whatever sourceRoot it's given, with zero dependency on
// that root having gone through a verified ingest first). So "just make
// proxies, skip the full copy" is really just: scan the card, pick a proxy
// folder, enqueue straight from the card's own mount path as the source --
// never touching startOffload/the job-history "ingest" concept at all.
export default function ProxiesOnlyAction({ c, device }) {
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [proxyRoot, setProxyRoot] = useState('');
  const [proxyJobs, setProxyJobs] = useState(null);
  const [proxyError, setProxyError] = useState(null);

  useEffect(() => {
    if (!proxyJobs) return;
    const offJobStarted = window.electronAPI.proxyQueue.onJobStarted((job) => {
      setProxyJobs((prev) => prev && prev.map(j => (j.id === job.id ? job : j)));
    });
    const offJobComplete = window.electronAPI.proxyQueue.onJobComplete((job) => {
      setProxyJobs((prev) => prev && prev.map(j => (j.id === job.id ? job : j)));
    });
    return () => { offJobStarted(); offJobComplete(); };
  }, [proxyJobs]);

  const start = useCallback(async () => {
    setScanError(null);
    setProxyError(null);
    const destResult = await window.electronAPI.cardIngest.chooseDestination();
    if (!destResult?.success || !destResult.path) return;
    setProxyRoot(destResult.path);

    setScanning(true);
    const scanResult = await window.electronAPI.cardIngest.scan(device.mountPath);
    setScanning(false);
    if (!scanResult?.success) {
      setScanError(scanResult?.error || 'Scan failed');
      return;
    }

    const files = scanResult.data.files.map(f => ({ relativePath: f.relativePath, size: f.size }));
    const enqueueResult = await window.electronAPI.proxyQueue.enqueue(files, destResult.path, device.mountPath);
    if (enqueueResult?.success) setProxyJobs(enqueueResult.data);
    else setProxyError(enqueueResult?.error || 'Failed to start proxy generation');
  }, [device.mountPath]);

  if (proxyJobs) {
    const done = proxyJobs.filter(j => j.status === 'completed' || j.status === 'failed' || j.status === 'cancelled').length;
    return (
      <div style={{ marginTop: '8px' }}>
        <div style={{ fontSize: '10.5px', color: c.muted, marginBottom: '4px' }}>
          Generating proxies into <span style={{ fontFamily: 'monospace' }}>{proxyRoot}</span> — {done}/{proxyJobs.length} done
        </div>
        <ProxyJobList c={c} jobs={proxyJobs} />
      </div>
    );
  }

  return (
    <div style={{ marginTop: '8px' }}>
      <button onClick={start} disabled={scanning} style={{ ...btnStyle(c, 'default'), opacity: scanning ? 0.6 : 1 }}>
        {scanning ? <Loader2 size={12} style={{ animation: 'cardIngestSpin 1s linear infinite' }} /> : null} Generate proxies only (no copy)
      </button>
      {(scanError || proxyError) && <div style={{ fontSize: '11px', color: c.err, marginTop: '4px' }}>{scanError || proxyError}</div>}
    </div>
  );
}
